
import React, { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

interface MaskedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  mask: string; // e.g., "999-999" where 9 = digit
  className?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function MaskedInput({ mask, className, value = "", onChange, ...props }: MaskedInputProps) {
  const [displayValue, setDisplayValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const isUpdatingRef = useRef(false);

  // Функция для применения маски
  const applyMask = (inputValue: string): string => {
    // Извлекаем только цифры
    const digits = inputValue.replace(/[^\d]/g, "");
    let maskedValue = "";
    let digitIndex = 0;

    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
      if (mask[i] === "9") {
        maskedValue += digits[digitIndex];
        digitIndex++;
      } else {
        maskedValue += mask[i];
      }
    }

    return maskedValue;
  };

  // Синхронизация с внешним значением
  useEffect(() => {
    if (!isUpdatingRef.current) {
      const masked = applyMask(value || "");
      setDisplayValue(masked);
    }
  }, [value, mask]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const maskedValue = applyMask(inputValue);
    
    // Предотвращаем циклические обновления
    isUpdatingRef.current = true;
    setDisplayValue(maskedValue);
    
    // Извлекаем только цифры для передачи в родительский компонент
    const digitsOnly = inputValue.replace(/[^\d]/g, "");
    
    // Создаем событие с очищенным значением (только цифры)
    const newEvent = {
      ...e,
      target: {
        ...e.target,
        value: maskedValue // Отправляем маскированное значение
      }
    };

    onChange?.(newEvent as React.ChangeEvent<HTMLInputElement>);
    
    // Сбрасываем флаг после обновления
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Allow backspace, delete, tab, escape, enter, and navigation keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey) ||
        (e.keyCode === 67 && e.ctrlKey) ||
        (e.keyCode === 86 && e.ctrlKey) ||
        (e.keyCode === 88 && e.ctrlKey)) {
      return;
    }
    // Allow only digits
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={cn(className)}
    />
  );
}
